// /////////////////////////////////////////////////////////////
// [ファイル] SourceQualityPlatform.tsx
// [概要] ソース品質管理プラットフォームのメインページ (フェーズ1 MVP)。
//        ソースをシンボル単位で参照し、品質メタ情報 (状態 / 担当 / タグ /
//        課題 / AI コメント) を管理する 3 ペイン UI。
//        現状はサンプルデータで動作し、操作感の確認を目的とする。
//        バックエンド (パーサ → Firestore) は後続フェーズで配線する。
// /////////////////////////////////////////////////////////////
import React, { useMemo, useState } from 'react';
import { Box, Typography, TextField, Fab, Select, MenuItem, Paper } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../constants/icons';
import { STANDARD_FAB_CTA_SX } from '../constants/buttonStyles';
import { useNotification } from '../providers/NotificationProvider';
import { useSourceQuality } from '../hooks/useSourceQuality';
import SymbolTree from '../components/sourceQuality/SymbolTree';
import CodeViewer from '../components/sourceQuality/CodeViewer';
import SymbolMetaPanel from '../components/sourceQuality/SymbolMetaPanel';
import {
  ReviewStatus,
  SourceProject,
  REVIEW_STATUS_LABEL,
  REVIEW_STATUS_ORDER,
} from '../types/sourceQuality';
import { PROJECT_LABEL, PROJECT_ORDER } from '../components/sourceQuality/statusVisuals';

type StatusFilter = ReviewStatus | 'all';
type ProjectFilter = SourceProject | 'all';

export default function SourceQualityPlatform() {
  const notify = useNotification();
  const {
    modules,
    selectedSymbolId,
    setSelectedSymbolId,
    selectedSymbol,
    setReviewStatus,
    setAssignee,
    addTag,
    removeTag,
    addIssue,
    removeIssue,
    toggleIssueStatus,
  } = useSourceQuality();

  const [searchWord, setSearchWord] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [projectFilter, setProjectFilter] = useState<ProjectFilter>('all');

  // ツリー表示用にフィルタを適用 (シンボルが残るモジュールのみ表示)。
  const filteredModules = useMemo(() => {
    const word = searchWord.trim().toLowerCase();
    return modules
      .filter((m) => projectFilter === 'all' || m.project === projectFilter)
      .map((m) => ({
        ...m,
        symbols: m.symbols.filter((s) => {
          if (statusFilter !== 'all' && s.review_status !== statusFilter) return false;
          if (!word) return true;
          return (
            s.name.toLowerCase().includes(word) ||
            s.qualified_name.toLowerCase().includes(word) ||
            m.file_path.toLowerCase().includes(word)
          );
        }),
      }))
      .filter((m) => m.symbols.length > 0);
  }, [modules, searchWord, statusFilter, projectFilter]);

  const handleIngest = () => {
    notify.info('取り込みはバックエンド配線後に有効化されます（現在はサンプルデータ）');
  };

  return (
    <Box sx={{ p: 3, width: '98%', margin: '0 auto' }}>
      {/* ヘッダ */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          px: 1,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5, color: blueGrey[500] }}
          >
            <Icons.AccountTree /> ソース品質管理
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            ソースをシンボル単位で参照し、AI 要約・課題・品質状態を管理します（フェーズ1 サンプルデータ）
          </Typography>
        </Box>

        <Fab variant="extended" onClick={handleIngest} sx={STANDARD_FAB_CTA_SX}>
          <Icons.Sync />
          取り込み実行
        </Fab>
      </Box>

      {/* フィルタ */}
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, px: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="シンボル名 / ファイルパスで絞り込み"
          value={searchWord}
          onChange={(e) => setSearchWord(e.target.value)}
          sx={{ minWidth: 280, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.5 } }}
          InputProps={{
            startAdornment: <Icons.Search fontSize="small" sx={{ mr: 0.5, color: 'text.disabled' }} />,
          }}
        />
        <Select
          size="small"
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value as ProjectFilter)}
          sx={{ fontSize: '0.8rem', minWidth: 170 }}
        >
          <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>すべての領域</MenuItem>
          {PROJECT_ORDER.map((p) => (
            <MenuItem key={p} value={p} sx={{ fontSize: '0.8rem' }}>
              {PROJECT_LABEL[p]}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          sx={{ fontSize: '0.8rem', minWidth: 150 }}
        >
          <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>すべての状態</MenuItem>
          {REVIEW_STATUS_ORDER.map((st) => (
            <MenuItem key={st} value={st} sx={{ fontSize: '0.8rem' }}>
              {REVIEW_STATUS_LABEL[st]}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* 3 ペイン */}
      <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, height: 'calc(100vh - 220px)' }}>
        <Paper variant="outlined" sx={{ width: 290, flexShrink: 0, overflowY: 'auto', borderRadius: 2 }}>
          <SymbolTree
            modules={filteredModules}
            selectedSymbolId={selectedSymbolId}
            onSelect={setSelectedSymbolId}
          />
        </Paper>

        <Paper variant="outlined" sx={{ flexGrow: 1, overflow: 'hidden', borderRadius: 2 }}>
          <CodeViewer symbol={selectedSymbol} />
        </Paper>

        <Paper variant="outlined" sx={{ width: 340, flexShrink: 0, overflow: 'hidden', borderRadius: 2 }}>
          {selectedSymbol ? (
            <SymbolMetaPanel
              symbol={selectedSymbol}
              onChangeStatus={(st) => setReviewStatus(selectedSymbol.symbol_id, st)}
              onChangeAssignee={(a) => setAssignee(selectedSymbol.symbol_id, a)}
              onAddTag={(t) => addTag(selectedSymbol.symbol_id, t)}
              onRemoveTag={(t) => removeTag(selectedSymbol.symbol_id, t)}
              onAddIssue={(input) => addIssue(selectedSymbol.symbol_id, input)}
              onRemoveIssue={(id) => removeIssue(selectedSymbol.symbol_id, id)}
              onToggleIssue={(id) => toggleIssueStatus(selectedSymbol.symbol_id, id)}
            />
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
              }}
            >
              <Typography variant="body2">シンボルを選択すると品質情報を編集できます</Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
